import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface Influencer {
  id: string;
  username: string;
  full_name: string;
  subscribers: number;
  views_avg: number;
  likes_avg: number;
  comments_avg: number;
  shares_avg: number;
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

export default function InfluencerTable() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<keyof Influencer>('subscribers');
  const [sortDesc, setSortDesc] = useState(true);
  const [showClickHint, setShowClickHint] = useState(true);
  const pageSize = 10;

  // Hide click hint after 10 seconds
  useEffect(() => {
    if (showClickHint) {
      const timer = setTimeout(() => {
        setShowClickHint(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [showClickHint]);

  const { data, isLoading, error } = useQuery(
    ['influencers', page, sortBy, sortDesc],
    async () => {
      const start = (page - 1) * pageSize;
      const end = start + pageSize - 1;

      const { data, error } = await supabase
        .from('influencers')
        .select()
        .order(sortBy, { ascending: !sortDesc })
        .range(start, end);

      if (error) throw error;
      return data;
    }
  );

  const handleSort = (column: keyof Influencer) => {
    if (sortBy === column) {
      setSortDesc(!sortDesc);
    } else {
      setSortBy(column);
      setSortDesc(true);
    }
  };

  const handleRowClick = (username: string) => {
    setShowClickHint(false);
    navigate(`/inbox/${username}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-4 bg-red-50 rounded-lg">
        Error loading influencers data
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-gray-600 p-4 text-center">
        No influencers found
      </div>
    );
  }

  const columns: { key: keyof Influencer; label: string }[] = [
    { key: 'username', label: 'Username' },
    { key: 'full_name', label: 'Full Name' },
    { key: 'subscribers', label: 'Subscribers' },
    { key: 'views_avg', label: 'Views' },
    { key: 'likes_avg', label: 'Likes' },
    { key: 'comments_avg', label: 'Comments' },
    { key: 'shares_avg', label: 'Shares' }
  ];

  return (
    <div className="relative">
      {showClickHint && (
        <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg px-4 py-3 flex items-center space-x-2 animate-bounce">
          <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
          </svg>
          <span className="text-sm font-medium text-gray-700">
            Click any row to start a conversation with that influencer!
          </span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => handleSort(key)}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center space-x-1">
                      <span>{label}</span>
                      {sortBy === key && (
                        <span>{sortDesc ? '↓' : '↑'}</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((influencer: Influencer) => (
                <tr 
                  key={influencer.id} 
                  onClick={() => handleRowClick(influencer.username)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {influencer.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {influencer.full_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatNumber(influencer.subscribers)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatNumber(influencer.views_avg)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatNumber(influencer.likes_avg)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatNumber(influencer.comments_avg)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatNumber(influencer.shares_avg)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {page}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={data.length < pageSize}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}